import { calculateAge } from '@/helpers/fdate';
import { validateCep } from '@/helpers/cep';
import axios from 'axios';
import { date as dateRegex } from 'helpers/regex';
import { cpf as cpfValidate } from 'helpers/validate';
import * as Yup from 'yup';
import { NextPaymentMethod } from '@/types/reservation';
import * as Regex from '@/helpers/regex';

export interface InitialValues {
  paymentMethod: NextPaymentMethod;
  ddi: string;
  firstname: string;
  birthDate: string;
  nationality: string;
  cnh: string;
  email: string;
  emailConfirm: string;
  installments: number;
  cpf: string;
  phone: string;
  numeroTelefone: string;
  cep: string;
  cupom: string;
  cardNumber: string;
  holder: string;
  expirationDate: string;
  securityCode: string;
  pre_payment_terms: boolean;
  credit_card_caucao_required_terms: boolean;
  credit_card_required_terms: boolean;
  pix_payment_terms: boolean;
  terms: boolean;
  ofertas: boolean;
  promo_code: string;
  reservationUniqueId: string;
}

export interface SavedValues extends InitialValues {
  rate_id: string;
  numericPhone?: string;
}

export const FORM_NAME = 'condutor-form';

declare module 'yup' {
  interface StringSchema {
    cpf(): Yup.StringSchema;
    overEighteen(): Yup.StringSchema;
  }
}

export interface ConductorForm {
  firstname: string;
  birthDate: string;
  cpf: string;
  phone: string;
  email: string;
  emailConfirm: string;
  nationality: string;
  terms: string;
  ofertas: string;
  promo_code: string;
}

const validarCEP = async (cep: string) => {
  return await validateCep(cep);
};

Yup.addMethod<Yup.StringSchema>(Yup.string, 'cpf', function cpf() {
  return this.test({
    message: 'CPF inválido',
    name: 'cpf',
    test: payload => {
      if (!payload) return true;
      return cpfValidate(payload);
    },
  });
});

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'overEighteen',
  function overEighteen() {
    return this.test({
      message: 'Você precisa ter mais de 18 anos',
      name: 'overEighteen',
      test: payload => {
        if (typeof payload === 'undefined') return true;

        if (dateRegex.test(payload)) {
          const age = calculateAge(payload);
          return age >= 18 && age < 80;
        }

        return false;
      },
    });
  },
);

const conductorForm = Yup.object().shape({
  paymentMethod: Yup.string(),
  firstname: Yup.string()
    .matches(
      /^[a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ\s]+$/,
      'Seu nome não pode conter números ou caracteres especiais',
    )
    .test(
      'full-name',
      'Digite seu nome completo (nome e sobrenome)',
      value => !!value && value.trim().split(/\s+/).length >= 2,
    )
    .required('Nome é obrigatório'),
  nationality: Yup.string()
    .oneOf(['brasileiro', 'estrangeiro'], 'Nacionalidade inválida')
    .required('Selecione a nacionalidade'),
  birthDate: Yup.string()
    .overEighteen()
    .matches(dateRegex, 'Data inválida')
    .required('Data é obrigatório'),
  cpf: Yup.string()
    .cpf()
    .when('nationality', {
      is: 'brasileiro',
      then: Yup.string().required('CPF é obrigatório').cpf(),
    }),
  phone: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .matches(/^\+\d{1,3} \(\d{2}\) 9 \d{4}-\d{4}$/, 'Telefone inválido')
      .required('Telefone é obrigatório'),
    otherwise: Yup.string()
      .matches(
        /^\+\d{1,3} \d{1,15}$/,
        'Telefone deve conter apenas números e no máximo 15 dígitos',
      )
      .required('Telefone é obrigatório'),
  }),
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  emailConfirm: Yup.string()
    .email('E-mail inválido')
    .test('email-match', 'E-mails não coincidem', function (payload) {
      return this.parent.email === payload;
    })
    .required('Confirme o E-mail'),
  cep: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .required('CEP é obrigatório')
      .test('valida-cep', 'CEP inválido', async value => {
        if (value) {
          const cep = value.replaceAll(/\D/g, '');
          if (cep.length === 8) {
            return await validarCEP(cep);
          }
        }
        return false;
      }),
  }),
  promo_code: Yup.string(),
});

const conductorCreditCardForm = Yup.object().shape({
  paymentMethod: Yup.string(),
  firstname: Yup.string()
    .matches(
      /^[a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ\s]+$/,
      'Seu nome não pode conter números ou caracteres especiais',
    )
    .test(
      'full-name',
      'Digite seu nome completo (nome e sobrenome)',
      value => !!value && value.trim().split(/\s+/).length >= 2,
    )
    .required('Nome é obrigatório'),
  nationality: Yup.string()
    .oneOf(['brasileiro', 'estrangeiro'], 'Nacionalidade inválida')
    .required('Selecione a nacionalidade'),
  birthDate: Yup.string()
    .overEighteen()
    .matches(dateRegex, 'Data inválida')
    .required('Data é obrigatório'),
  cpf: Yup.string()
    .cpf()
    .when('nationality', {
      is: 'brasileiro',
      then: Yup.string().required('CPF é obrigatório').cpf(),
    }),
  phone: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .matches(/^\+\d{1,3} \(\d{2}\) 9 \d{4}-\d{4}$/, 'Telefone inválido')
      .required('Telefone é obrigatório'),
    otherwise: Yup.string()
      .matches(
        /^\+\d{1,3} \d{1,15}$/,
        'Telefone deve conter apenas números e no máximo 15 dígitos',
      )
      .required('Telefone é obrigatório'),
  }),
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  emailConfirm: Yup.string()
    .email('E-mail inválido')
    .test('email-match', 'E-mails não coincidem', function (payload) {
      return this.parent.email === payload;
    })
    .required('Confirme o E-mail'),
  cep: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .required('CEP é obrigatório')
      .test('valida-cep', 'CEP inválido', async value => {
        if (value) {
          const cep = value.replaceAll(/\D/g, '');
          if (cep.length === 8) {
            return await validarCEP(cep);
          }
        }
        return false;
      }),
  }),
  installments: Yup.number()
    .min(1, 'Número de parcelas é menor que o mínimo permitido')
    .required(
      'O número de parcelas é obrigatório para pagamentos com cartão de crédito',
    ),
  holder: Yup.string().required(
    'O nome do titular do cartão é obrigatório para pagamentos com cartão de crédito',
  ),
  terms: Yup.boolean()
    .oneOf([true], 'Aceite os termos')
    .required('Aceite os termos'),
  ofertas: Yup.boolean().oneOf([true, false], 'Ofertas inválido'),
  promo_code: Yup.string(),
});

const paymentForm = Yup.object().shape({
  paymentMethod: Yup.string(),
  installments: Yup.number()
    .min(1, 'Número de parcelas é menor que o mínimo permitido')
    .required(
      'O número de parcelas é obrigatório para pagamentos com cartão de crédito',
    ),
  holder: Yup.string().required(
    'O nome do titular do cartão é obrigatório para pagamentos com cartão de crédito',
  ),
  terms: Yup.boolean()
    .oneOf([true], 'Aceite os termos')
    .required('Aceite os termos'),
  ofertas: Yup.boolean().oneOf([true, false], 'Ofertas inválido'),
});

const paymentFormPosPaid = Yup.object().shape({
  terms: Yup.boolean()
    .oneOf([true], 'Aceite os termos')
    .required('Aceite os termos'),
  ofertas: Yup.boolean().oneOf([true, false], 'Ofertas inválido'),
});

const baseRenterSchema = Yup.object().shape({
  firstname: Yup.string()
    .matches(
      /^[a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ\s]+$/,
      "Seu nome não pode conter números ou caracteres especiais"
    )
    .test(
      "full-name",
      "Digite seu nome completo (nome e sobrenome)",
      (value) => !!value && value.trim().split(/\s+/).length >= 2,
    )
    .required("Nome é obrigatório"),
  birthDate: Yup.string()
    .matches(Regex.date, "Data inválida (DD/MM/YYYY)")
    .required("Data de nascimento é obrigatória"),
  cpf: Yup.string().when("nationality", {
    is: (nationality: string | undefined | null) => nationality === "brasileiro" || !nationality,
    then: Yup.string().required("CPF é obrigatório").cpf(),
    otherwise: Yup.string().nullable(),
  }),
  phone: Yup.string().when("nationality", {
    is: (nationality: string | undefined | null) => nationality === "brasileiro" || !nationality,
    then: Yup.string()
      .matches(
        /^\+\d{1,3} \(\d{2}\) 9 \d{4}-\d{4}$/,
        "Telefone inválido (Ex: +55 (XX) 9 XXXX-XXXX)"
      )
      .required("Telefone é obrigatório"),
    otherwise: Yup.string()
      .matches(/^\+\d{1,3} \d{1,15}$/, "Telefone inválido (Ex: +XXX XXXXXXXXXX)")
      .required("Telefone é obrigatório"),
  }),
  email: Yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  nationality: Yup.string().required("Nacionalidade é obrigatória"),
  cep: Yup.string().when("nationality", {
    is: (nationality: string | undefined | null) => nationality === "brasileiro" || !nationality,
    then: Yup.string()
      .matches(/^\d{5}-\d{3}$/, "CEP inválido (XXXXX-XXX)")
      .required("CEP é obrigatório"),
    otherwise: Yup.string().nullable(),
  }),
})

const renterForm = Yup.object().shape({
  firstname: Yup.string()
    .matches(
      /^[a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ\s]+$/,
      'Seu nome não pode conter números ou caracteres especiais',
    )
    .test(
      'full-name',
      'Digite seu nome completo (nome e sobrenome)',
      value => !!value && value.trim().split(/\s+/).length >= 2,
    )
    .required('Nome é obrigatório'),
  nationality: Yup.string()
    .oneOf(['brasileiro', 'estrangeiro'], 'Nacionalidade inválida')
    .required('Selecione a nacionalidade'),
  birthDate: Yup.string()
    .overEighteen()
    .matches(dateRegex, 'Data inválida')
    .required('Data é obrigatório'),
  cpf: Yup.string()
    .cpf()
    .when('nationality', {
      is: 'brasileiro',
      then: Yup.string().required('CPF é obrigatório').cpf(),
    }),
  phone: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .matches(/^\+\d{1,3} \(\d{2}\) 9 \d{4}-\d{4}$/, 'Telefone inválido')
      .required('Telefone é obrigatório'),
    otherwise: Yup.string()
      .matches(
        /^\+\d{1,3} \d{1,15}$/,
        'Telefone deve conter apenas números e no máximo 15 dígitos',
      )
      .required('Telefone é obrigatório'),
  }),
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  cep: Yup.string().when('nationality', {
    is: 'brasileiro',
    then: Yup.string()
      .required('CEP é obrigatório')
      .test('valida-cep', 'CEP inválido', async value => {
        if (value) {
          const cep = value.replaceAll(/\D/g, '');
          if (cep.length === 8) {
            return await validarCEP(cep);
          }
        }
        return false;
      }),
  }),
});

export { conductorForm, conductorCreditCardForm, paymentForm, paymentFormPosPaid, renterForm, baseRenterSchema };
